import {
  createPlugin,
  createComponentExtension,
} from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import {
  DYNATRACE_APPLICATION_ANNOTATION,
  DYNATRACE_CLUSTER_ANNOTATION,
  DYNATRACE_SERVICE_ANNOTATION,
} from './types';

// Re-export for backwards compatibility
export {
  DYNATRACE_APPLICATION_ANNOTATION,
  DYNATRACE_CLUSTER_ANNOTATION,
  DYNATRACE_SERVICE_ANNOTATION,
};

/**
 * Check if Dynatrace observability is available for an entity.
 * Returns true if the entity has any Dynatrace observability annotation.
 *
 * @public
 */
export const isDynatraceAvailable = (entity: Entity): boolean => {
  const annotations = entity.metadata?.annotations;
  return Boolean(
    annotations?.[DYNATRACE_SERVICE_ANNOTATION] ||
    annotations?.[DYNATRACE_APPLICATION_ANNOTATION] ||
    annotations?.[DYNATRACE_CLUSTER_ANNOTATION],
  );
};

/**
 * The EMIS Observability plugin for Backstage.
 *
 * Provides components for displaying Dynatrace metrics including
 * response time percentiles, throughput, failure rate, and HTTP errors.
 *
 * @public
 */
export const emisObservabilityPlugin = createPlugin({
  id: 'emis-observability',
});

/**
 * A content component for displaying Dynatrace observability metrics.
 *
 * This component automatically reads the entity from context and displays
 * response time, throughput, failure rate, and HTTP error metrics.
 *
 * @public
 */
export const EntityObservabilityContent = emisObservabilityPlugin.provide(
  createComponentExtension({
    name: 'EntityObservabilityContent',
    component: {
      lazy: () =>
        import('./components/EntityObservabilityContent').then(
          m => m.EntityObservabilityContent,
        ),
    },
  }),
);

/**
 * A card component for displaying response time percentiles (p50, p90, p99).
 *
 * @public
 */
export const ResponseTimePercentilesCard = emisObservabilityPlugin.provide(
  createComponentExtension({
    name: 'ResponseTimePercentilesCard',
    component: {
      lazy: () =>
        import('./components/ResponseTimePercentilesCard').then(
          m => m.ResponseTimePercentilesCard,
        ),
    },
  }),
);

/**
 * A card component for displaying throughput metrics.
 *
 * @public
 */
export const ThroughputCard = emisObservabilityPlugin.provide(
  createComponentExtension({
    name: 'ThroughputCard',
    component: {
      lazy: () =>
        import('./components/ThroughputCard').then(m => m.ThroughputCard),
    },
  }),
);

/**
 * A card component for displaying failure rate metrics.
 *
 * @public
 */
export const FailureRateCard = emisObservabilityPlugin.provide(
  createComponentExtension({
    name: 'FailureRateCard',
    component: {
      lazy: () =>
        import('./components/FailureRateCard').then(m => m.FailureRateCard),
    },
  }),
);

/**
 * A card component for displaying HTTP error counts (4xx, 5xx).
 *
 * @public
 */
export const HttpErrorsCard = emisObservabilityPlugin.provide(
  createComponentExtension({
    name: 'HttpErrorsCard',
    component: {
      lazy: () =>
        import('./components/HttpErrorsCard').then(m => m.HttpErrorsCard),
    },
  }),
);
